<script setup lang="ts">
import { AnimatePresence } from 'motion-v'
import AuditForm from '@/components/AuditForm.vue'
import AuditReport from '@/components/AuditReport.vue'
import { useAuditStore } from '@/stores/audit'

const store = useAuditStore()

const checks = [
  {
    name: 'DNS Resolution',
    detail: 'Whether the domain resolves at all, and to how many IP addresses.',
  },
  {
    name: 'HTTPS & Certificate',
    detail:
      'Whether HTTPS is reachable, whether the TLS handshake succeeds, who issued the certificate, the TLS version, and how many days until it expires — a warning under 30 days, a failure under 14.',
  },
  {
    name: 'HTTP Response & Redirects',
    detail:
      'The status code, how many redirect hops it takes to land, and whether the final URL is still plain HTTP. Three hops is a warning; more than three is a failure.',
  },
  {
    name: 'Security Headers',
    detail:
      'Six headers browsers look for: Strict-Transport-Security, Content-Security-Policy, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy.',
  },
  {
    name: 'Passive Vulnerability Signals',
    detail:
      'Whether the Server or X-Powered-By header gives away software versions, whether directory listing is switched on, and whether five common backup or secret paths are reachable — /.env, /.git/config, /backup.zip, /backup.sql, /wp-config.php.bak.',
  },
]
</script>

<template>
  <section class="page-header">
    <p class="section-marker">Security audit</p>
    <h1 class="page-title display">Is your website safe?</h1>
    <p class="page-lede">
      I run five checks against your site: where your DNS points, whether your HTTPS certificate is
      valid and how many days it has left, how your redirects behave, which security headers are
      set, and whether anything is reachable that shouldn't be. A customer who hits a certificate
      warning or stumbles onto an exposed backup file doesn't stay to ask why — they leave. You get
      a score and what to fix first, on screen, in about 15 seconds.
    </p>
  </section>

  <section class="audit-card">
    <AuditForm />
    <AnimatePresence>
      <AuditReport
        v-if="store.status === 'success' && store.report"
        :key="store.report.host + store.report.timestamp"
        :report="store.report"
        :audit-id="store.auditId"
      />
    </AnimatePresence>
  </section>

  <section class="checks-section" aria-labelledby="checks-heading">
    <header class="checks-header">
      <h2 id="checks-heading">What I check</h2>
      <p class="checks-caption">Five checks, run against every submission.</p>
    </header>
    <div class="check-table-wrap">
      <table class="check-table">
        <thead>
          <tr>
            <th scope="col">Check</th>
            <th scope="col">What it looks at</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in checks" :key="c.name">
            <th scope="row">{{ c.name }}</th>
            <td>{{ c.detail }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.page-header {
  max-width: 40em;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin: 2rem 0 2.25rem;
}

.page-title {
  margin: 0;
  font-size: clamp(1.9rem, 4vw, 2.7rem);
  color: var(--text);
}

.page-lede {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  font-variation-settings: 'opsz' 18;
}

.audit-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 2rem;
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.checks-section {
  margin: 3rem 0 2.5rem;
}

.checks-header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
}

.checks-header h2 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text);
  font-variation-settings: 'opsz' 36;
}

.checks-caption {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  color: var(--text-faint);
}

.check-table-wrap {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  overflow-x: auto;
}

.check-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.check-table th,
.check-table td {
  text-align: left;
  padding: 0.85rem 1.1rem;
  vertical-align: top;
}

.check-table thead th {
  background: var(--surface-2);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);
}

.check-table tbody th {
  width: 15rem;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 0.98rem;
  color: var(--text);
  font-variation-settings: 'opsz' 20;
}

.check-table tbody td {
  color: var(--text-muted);
  line-height: 1.55;
}

.check-table tbody tr:not(:last-child) th,
.check-table tbody tr:not(:last-child) td {
  border-bottom: 1px solid var(--border);
}

@media (max-width: 600px) {
  .audit-card {
    padding: 1.25rem;
  }
  .check-table tbody th {
    width: 9rem;
  }
  .check-table th,
  .check-table td {
    padding: 0.7rem 0.85rem;
    font-size: 0.86rem;
  }
}
</style>
